import React, { useMemo } from "react";
import { Icon } from "@clarkinc/zest-design-system/ui";
import { cls } from "cls";
import Logo from "~/PlusPage/components/Logo";
import WelcomeMessage from "./WelcomeMessage";

interface BaseProps {
  modalId: string;
  onClose: () => void;
  welcomeMessageIsVisible: boolean;
}

const PlusCaptionHeader = (
  <div className="text-center">
    <Logo />
    <p className="mt-3 mb-6 px-4 text-xl font-normal leading-tight text-white">
      Unlimited <b>FREE</b> shipping, expedited order processing, and a savings
      guarantee. All for just $99/month.
    </p>
  </div>
);

const modalImages = [
  {
    src: "//cdnimg.webstaurantstore.com/uploads/design/2025/7/Plus-Modal-Customers/plus-modal-customer-1.png",
    caption: (
      <>
        <blockquote>
          “The Plus program is saving us a ton of money. At $99 a month, it paid
          for itself in literally the first week we had it.”
        </blockquote>
        <cite>— John S. of John Serock Catering</cite>
      </>
    ),
  },
  {
    src: "//cdnimg.webstaurantstore.com/uploads/design/2025/7/Plus-Modal-Customers/plus-modal-customer-2.png",
    caption: (
      <>
        <blockquote>
          “We have saved thousands and thousands of dollars not just on product
          but on shipping, too.”
        </blockquote>
        <cite>— Aaron R. of Dry Stock Coffee</cite>
      </>
    ),
  },
  {
    src: "//cdnimg.webstaurantstore.com/uploads/design/2025/7/Plus-Modal-Customers/plus-modal-customer-3.png",
    caption: (
      <>
        <blockquote>
          “Everything that I’ve looked for I cannot find a better price
          somewhere else.”
        </blockquote>
        <cite>— MJ S. of Agora Downtown Coffee Shop</cite>
      </>
    ),
  },
];

const InitialContent = () => {
  const undoScreenReaderOnlyClasses =
    "lm:static lm:w-auto lm:h-auto lm:overflow-visible lm:clip-auto lm:whitespace-normal lm:p-0 lm:m-0";
  const randomImage = useMemo(() => {
    return modalImages[Math.floor(Math.random() * modalImages.length)];
  }, []) as (typeof modalImages)[number];

  return (
    <>
      {/* Mobile View */}
      <div className="visible lm:invisible lm:h-0">
        <figure data-testid="modalImage">
          <source
            media="(min-width: 980px)"
            srcSet="//cdnimg.webstaurantstore.com/uploads/design/2024/6/PlusPage/plus-marketing-benefits-v2.svg"
          />
          <img
            src="//cdnimg.webstaurantstore.com/uploads/design/2024/6/PlusPage/plus-marketing-benefits-v2.svg"
            alt=""
          />
          <figcaption className="sr-only">
            <h2>Webstaurant Plus</h2>
            <p>
              Unlimited free shipping, expedited order processing, and a savings
              guarantee. All for just $99/month.
            </p>
            <p>Free Shipping</p>
            <p>Guaranteed Savings</p>
            <p>Get Your Items Faster</p>
          </figcaption>
        </figure>
      </div>
      {/* Desktop View */}
      <div className="lm:visible invisible h-0 lm:flex flex-col">
        {PlusCaptionHeader}
        <figure data-testid="modalImage" className="m-0 mb-5">
          <source media="(min-width: 980px)" srcSet={randomImage.src} />
          <img src={randomImage.src} alt="" />
          <figcaption className="sr-only">{randomImage.caption}</figcaption>
        </figure>
        <p
          className={`sr-only ${undoScreenReaderOnlyClasses} text-xs font-normal text-blue-50 pt-6`}
        >
          Special offers only valid for first time subscribers. This program
          only allows for a single shipping address and may be updated once per
          payment cycle if benefit is unused. Enrollment is open only to
          addresses located in the 48 contiguous United States. WebstaurantStore
          reserves the right to modify the terms of this program and to cancel
          this program at any time.
        </p>
      </div>
    </>
  );
};

const Header = ({ onClose, welcomeMessageIsVisible, modalId }: BaseProps) => {
  return (
    <>
      <button
        aria-label="Close"
        className="fixed z-20 right-4 top-4 appearance-none bg-transparent border-none m-0 p-0 rounded opacity-100 hover:opacity-50 lm:font-normal"
        data-testid="closeButton"
        onClick={onClose}
        type="button"
      >
        <Icon
          className="fill-white"
          name="ri-close-line"
          size="small"
          theme="wss"
        />
      </button>
      <div
        className={cls(
          "bg-[#022E44] flex overflow-hidden relative",
          "lm:p-0 lm:border-b-0 lm:static lm:h-full",
          !welcomeMessageIsVisible && "p-10 lm:px-12 lm:pb-6 lm:pt-10",
          welcomeMessageIsVisible && "absolute inset-0 z-10",
        )}
      >
        <div
          className={cls(
            "relative w-full h-full",
            welcomeMessageIsVisible && "w-full align-center",
          )}
        >
          <div
            className={cls(
              "w-full h-full",
              welcomeMessageIsVisible
                ? "opacity-0 pointer-events-none"
                : "opacity-100",
            )}
          >
            <InitialContent />
          </div>
          {/* this transition handles the opacity transition (not the left to right transition) */}
          <div
            className={cls(
              "opacity-0 transition-opacity duration-[300ms] delay-[450ms] ease-in-out",
              welcomeMessageIsVisible && "opacity-100",
            )}
          >
            {/* This animation handles the scaling in, with different timing than opacity.
             Tailwind doesn't support stacking animations with
             different timing/duration so - we need this extra div to do that */}
            <div
              className={cls(
                "absolute inset-0 w-full h-full pointer-events-none",
                "transition-transform transform origin-center delay-[300ms] duration-[500ms] scale-[0.4]",
                welcomeMessageIsVisible &&
                  "flex justify-center items-center scale-100 pointer-events-auto",
              )}
            >
              <WelcomeMessage
                modalId={modalId}
                onClose={onClose}
                welcomeMessageIsVisible={welcomeMessageIsVisible}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
